<script lang="ts" setup>
// O sino de notificações. Um toque abre o aviso, como sempre. No celular, segurar faz aparecer a
// lixeira, e soltar o sino sobre ela o tira da tela (ver utils/sinoDeNotificacao.ts).
import IconeBell from "assets/img/icone-bell.png";
import { isMobile } from "@/shared/utils/mobile";
import {
  GESTO_PARADO,
  RAIO_DA_LIXEIRA_PX,
  TEMPO_PARA_SEGURAR_MS,
  centroAtualDoSino,
  encostarNoSino,
  moverDedoNoSino,
  pontosProximos,
  segurouOSino,
  type Ponto,
} from "@/utils/sinoDeNotificacao";

const props = defineProps<{ textoDaLixeira: string }>();
const emits = defineEmits(["ativar", "remover"]);

const gesto = shallowRef(GESTO_PARADO);
const iconeDaLixeira = ref<HTMLElement | null>(null);
const sobreLixeira = ref(false);
let temporizador: ReturnType<typeof setTimeout> | null = null;

const arrastando = computed(() => gesto.value.fase === "arrastando");

const estiloDoArraste = computed(() => {
  if (!arrastando.value) return undefined;
  const { x, y } = gesto.value.deslocamento;
  return {
    transform: `translate3d(${x}px, ${y}px, 0) scale(${sobreLixeira.value ? 0.8 : 1.15})`,
  };
});

function centroDe(elemento: Element): Ponto {
  const caixa = elemento.getBoundingClientRect();
  return { x: caixa.left + caixa.width / 2, y: caixa.top + caixa.height / 2 };
}

function pontoDe(toque: Touch): Ponto {
  return { x: toque.clientX, y: toque.clientY };
}

function encerrar() {
  if (temporizador) clearTimeout(temporizador);
  temporizador = null;
  gesto.value = GESTO_PARADO;
  sobreLixeira.value = false;
}

function aoEncostar(evento: TouchEvent) {
  encerrar();
  if (!isMobile() || evento.touches.length > 1) return;

  gesto.value = encostarNoSino(
    pontoDe(evento.touches[0]),
    centroDe(evento.currentTarget as Element)
  );
  temporizador = setTimeout(() => {
    gesto.value = segurouOSino(gesto.value);
  }, TEMPO_PARA_SEGURAR_MS);
}

function aoMover(evento: TouchEvent) {
  gesto.value = moverDedoNoSino(gesto.value, pontoDe(evento.touches[0]));
  if (gesto.value.fase === "parado") {
    // O dedo andou antes do tempo: a pessoa está rolando a página, e a rolagem segue normal.
    encerrar();
    return;
  }
  if (!arrastando.value) return;

  // A partir daqui o dedo é do sino: a página não rola junto.
  if (evento.cancelable) evento.preventDefault();

  sobreLixeira.value =
    !!iconeDaLixeira.value &&
    pontosProximos(
      centroAtualDoSino(gesto.value),
      centroDe(iconeDaLixeira.value),
      RAIO_DA_LIXEIRA_PX
    );
}

function aoSoltar(evento: TouchEvent) {
  const eraArraste = arrastando.value;
  const remover = eraArraste && sobreLixeira.value;
  encerrar();

  // Toque rápido: o navegador dispara o click, que abre o aviso. Depois de segurar, não.
  if (eraArraste && evento.cancelable) evento.preventDefault();
  if (remover) emits("remover");
}

// No celular, segurar a imagem abriria o menu do navegador ("Salvar imagem") e cortaria o gesto.
function aoPedirMenu(evento: Event) {
  if (isMobile()) evento.preventDefault();
}

onBeforeUnmount(encerrar);
</script>

<template>
  <img
    class="notify-bell"
    :class="{ 'notify-bell--arrastando': arrastando }"
    :style="estiloDoArraste"
    :src="IconeBell"
    alt="Bella Notifications"
    draggable="false"
    @click="emits('ativar')"
    @contextmenu="aoPedirMenu"
    @touchstart="aoEncostar"
    @touchmove="aoMover"
    @touchend="aoSoltar"
    @touchcancel="encerrar"
  />

  <Transition name="notify-lixeira">
    <div
      v-if="arrastando"
      class="notify-lixeira"
      :class="{ 'notify-lixeira--ativa': sobreLixeira }"
      aria-hidden="true"
    >
      <span ref="iconeDaLixeira" class="notify-lixeira-icone">
        <font-awesome :icon="['fas', 'trash']" />
      </span>
      <span class="notify-lixeira-texto">{{ props.textoDaLixeira }}</span>
    </div>
  </Transition>
</template>

<style scoped>
.notify-bell {
  position: fixed;
  width: 50px;
  top: 80%;
  margin-left: 28px;
  cursor: pointer;
  z-index: 9;
  /* Segurar o sino não abre o balão de "Salvar imagem" do iOS nem seleciona nada. */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  /* Solto fora da lixeira, o sino volta ao lugar. */
  transition: transform 200ms ease;
}

/* Acima do cabeçalho e da lixeira enquanto acompanha o dedo. */
.notify-bell--arrastando {
  z-index: 9998;
  transition: none;
}

.notify-lixeira {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 24px;
  z-index: 9997;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: none;
}

.notify-lixeira-icone {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.75);
  color: #ffffff;
  font-size: 24px;
  transition: transform 150ms ease, background-color 150ms ease;
}

.notify-lixeira--ativa .notify-lixeira-icone {
  background-color: #b20000;
  transform: scale(1.2);
}

.notify-lixeira-texto {
  padding: 2px 10px;
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.75);
  color: #ffffff;
  font-size: 13px;
}

.notify-lixeira-enter-active,
.notify-lixeira-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.notify-lixeira-enter-from,
.notify-lixeira-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
